/**
 * CRM translations (PRIVATE fork) — registered via registerLocaleMessages (S5-i18n)
 * in bootstrap.extensions.ts. Ported from the legacy i18n 'crm' namespace so t('crm.*')
 * resolves without editing the upstream en.ts/zh.ts bundles.
 */

export const crmEn = {
  title: 'CRM Overview',
  subtitle: 'Customer relationship management at a glance',

  // Shared stage labels
  stageDraft: 'Draft',
  stageSent: 'Sent',
  stageNegotiating: 'Negotiating',
  stageWon: 'Won',
  stageLost: 'Lost',
  stageCancelled: 'Cancelled',

  // Shared type labels
  typeQuotation: 'Quotation',
  typeSalesOrder: 'Sales Order',
  typeQuoteShort: 'Quote',
  typeOrderShort: 'Order',

  // Shared entity labels
  company: 'Company',
  contact: 'Contact',
  deal: 'Deal',
  interaction: 'Interaction',

  // Nav tabs
  overview: 'Overview',
  companies: 'Customers',
  contacts: 'Contacts',
  files: 'Files',
  deals: 'Deals',
  products: 'Products',
  followups: 'Follow-ups',

  // Home stats
  totalDeals: 'Total Deals',
  pipelineValue: 'Pipeline Value',
  wonRevenue: 'Won Revenue',
  newThisMonth: 'New This Month',
  dealPipeline: 'Deal Pipeline',
  noDealsYet: 'No deals yet',
  recentInteractions: 'Recent Interactions',
  noInteractionsYet: 'No interactions yet',

  // Company list
  searchCompanies: 'Search companies...',
  allCountries: 'All Countries',
  allIndustries: 'All Industries',
  noCompaniesFound: 'No companies found',
  adjustFilters: 'Try adjusting your filters',
  createFirstCompany: 'Create your first company to get started',
  companyDeleted: 'Company deleted',
  deleteCompany: 'Delete Company',
  deleteCompanyConfirm: 'Are you sure you want to delete "{name}"? This will also unlink all contacts.',

  // Company detail
  backToCompanies: 'Back to Companies',
  editCompany: 'Edit company',
  contactsTab: 'Contacts ({count})',
  interactionsTab: 'Interactions ({count})',
  dealsTab: 'Deals ({count})',
  companyNotFound: 'Company not found',
  companyNotFoundDesc: 'This company may have been deleted',

  // Company form
  newCompany: 'New Company',
  editCompanyTitle: 'Edit Company',
  companyName: 'Company Name',
  companyNamePlaceholder: 'Company name',
  domain: 'Domain',
  domainPlaceholder: 'example.com',
  website: 'Website',
  country: 'Country',
  countryPlaceholder: 'Country',
  industry: 'Industry',
  industryPlaceholder: 'Industry',
  phone: 'Phone',
  phonePlaceholder: '+49...',
  companySize: 'Size',
  sizeSmall: 'Small',
  sizeMedium: 'Medium',
  sizeLarge: 'Large',
  sizeEnterprise: 'Enterprise',
  address: 'Address',
  addressPlaceholder: 'Full address',
  notes: 'Notes',
  notesPlaceholder: 'Internal notes...',
  companyNameRequired: 'Company name is required',
  companyCreated: 'Company created',
  companyUpdated: 'Company updated',

  // Contact list
  searchContacts: 'Search contacts...',
  noContactsFound: 'No contacts found',
  adjustSearch: 'Try adjusting your search',
  createFirstContact: 'Create your first contact to get started',

  // Contact detail
  backToContacts: 'Back to Contacts',
  editContact: 'Edit contact',
  activityLogTab: 'Activity Log ({count})',
  contactNotFound: 'Contact not found',
  contactNotFoundDesc: 'This contact may have been deleted',

  // Contact form
  newContact: 'New Contact',
  editContactTitle: 'Edit Contact',
  fullName: 'Name',
  fullNamePlaceholder: 'Full name',
  email: 'Email',
  emailPlaceholder: 'email@example.com',
  jobTitle: 'Job Title',
  jobTitlePlaceholder: 'e.g. Purchasing Manager',
  department: 'Department',
  departmentPlaceholder: 'e.g. Procurement',
  noCompany: '— No Company —',
  nameRequired: 'Name is required',
  emailRequired: 'Email is required',
  contactCreated: 'Contact created',
  contactUpdated: 'Contact updated',

  // Deal list
  allStages: 'All Stages',
  allTypes: 'All Types',
  listView: 'List view',
  kanbanView: 'Kanban view',
  noDealsFound: 'No deals found',
  createFirstDeal: 'Create your first deal to get started',

  // Deal detail
  backToDeals: 'Back to Deals',
  dealNotFound: 'Deal not found',
  dealNotFoundDesc: 'This deal may have been deleted',
  advanceStage: 'Advance to {stage}',
  markAsLost: 'Mark as Lost',
  stageUpdated: 'Stage updated',
  dealMarkedLost: 'Deal marked as lost',
  failedUpdateStage: 'Failed to update stage',
  lineItems: 'Line Items',
  dealInfo: 'Deal Info',
  activityLog: 'Activity Log',

  // Interaction form
  logInteraction: 'Log Interaction',
  interactionTypeNote: 'Note',
  interactionTypeCall: 'Call',
  interactionTypeMeeting: 'Meeting',
  interactionTypeEmailOut: 'Email (Outbound)',
  interactionTypeOther: 'Other',
  subject: 'Subject',
  subjectPlaceholder: 'Brief summary...',
  content: 'Content',
  contentPlaceholder: 'Details...',
  contentRequired: 'Content is required',
  interactionLogged: 'Interaction logged',

  // Common
  source: 'Source',
  tags: 'Tags',
  createdAt: 'Created',
  updatedAt: 'Updated',
  amount: 'Amount',
  currency: 'Currency',
  probability: 'Probability',
  expectedClose: 'Expected Close',
  paymentTerms: 'Payment Terms',
  shippingTerms: 'Shipping Terms',
  saveFailed: 'Save failed',
  deleteFailed: 'Delete failed',

  // Customer (客户) fields
  customerNo: 'Customer No.',
  customerStatus: 'Status',
  agentLevel: 'Agent Level',
  quoteLevel: 'Quote Level',
  consignee: 'Consignee',
  registeredAt: 'Registered',
  lastFollowup: 'Last Follow-up',
  nextFollowup: 'Next Follow-up',
  salesAmount: 'Sales Amount',
  allStatuses: 'All Statuses',

  // Products (产品)
  searchProducts: 'Search products...',
  noProductsFound: 'No products found',
  createFirstProduct: 'Create your first product to get started',
  addProduct: 'Add Product',
  productNo: 'Product No.',
  productName: 'Product Name',
  unitPrice: 'Unit Price',
  category: 'Category',
  brand: 'Brand',
  dimensions: 'Dimensions',
  netWeight: 'Net Weight',
  hsCode: 'HS Code',
  cartonCapacity: 'Carton Capacity',
  productActive: 'Active',
  productInactive: 'Inactive',
  productCreated: 'Product created',
  productUpdated: 'Product updated',
  productDeleted: 'Product deleted',
  deleteProduct: 'Delete Product',
  nameCn: 'Chinese Name',
  nameEn: 'English Name',
  referencePriceNote: 'Reference list price — quoting is case-by-case',

  // Follow-ups (跟进)
  followupRadar: 'Follow-up Radar',
  followupsTab: 'Follow-ups ({count})',
  searchFollowups: 'Search follow-ups...',
  dueOnly: 'Due / upcoming only',
  allFollowups: 'All follow-ups',
  noFollowups: 'No follow-ups',
  noFollowupsDue: 'Nothing due — all caught up',
  logFollowup: 'Log Follow-up',
  purpose: 'Purpose',
  purposePlaceholder: 'e.g. confirm payment',
  result: 'Result',
  resultPlaceholder: 'What happened...',
  outcome: 'Outcome',
  nextGoal: 'Next Goal',
  nextGoalPlaceholder: 'Goal for the next touch...',
  followupCreated: 'Follow-up logged',
  customer: 'Customer',
  overdue: 'Overdue',
  upcoming: 'Upcoming',

  // Deal extras
  goodsSubtotal: 'Goods Subtotal',
  taxRate: 'Tax Rate',
  taxAmount: 'Tax',
  freight: 'Freight',
  bankFee: 'Bank Fee',
  discount: 'Discount',
  deposit: 'Deposit',
  balance: 'Balance',
  received: 'Received',
  deliveryMethod: 'Delivery (Incoterms)',
  paymentMethod: 'Payment Method',
  ourCompany: 'Our Company',
  salesRep: 'Sales Rep',
  owner: 'Owner',
  validUntil: 'Valid Until',
  expectedDelivery: 'Expected Delivery',
  shipTo: 'Ship To',
  adapter: 'Adapter',
  sourceQuote: 'From Quotation',
};

export const crmZh = {
  title: 'CRM 概览',
  subtitle: '客户关系管理一览',

  stageDraft: '草稿',
  stageSent: '已发送',
  stageNegotiating: '谈判中',
  stageWon: '已赢单',
  stageLost: '已丢单',
  stageCancelled: '已取消',

  typeQuotation: '报价单',
  typeSalesOrder: '销售订单',
  typeQuoteShort: '报价',
  typeOrderShort: '订单',

  company: '公司',
  contact: '联系人',
  deal: '交易',
  interaction: '互动记录',

  overview: '概览',
  companies: '客户',
  contacts: '联系人',
  files: '文件',
  deals: '交易',
  products: '产品',
  followups: '跟进',

  totalDeals: '交易总数',
  pipelineValue: '管线金额',
  wonRevenue: '赢单收入',
  newThisMonth: '本月新增',
  dealPipeline: '交易管线',
  noDealsYet: '暂无交易',
  recentInteractions: '最近互动',
  noInteractionsYet: '暂无互动记录',

  searchCompanies: '搜索公司...',
  allCountries: '所有国家',
  allIndustries: '所有行业',
  noCompaniesFound: '未找到公司',
  adjustFilters: '尝试调整筛选条件',
  createFirstCompany: '创建第一个公司以开始',
  companyDeleted: '公司已删除',
  deleteCompany: '删除公司',
  deleteCompanyConfirm: '确定要删除 "{name}" 吗？这将同时解除所有联系人的关联。',

  backToCompanies: '返回公司列表',
  editCompany: '编辑公司',
  contactsTab: '联系人 ({count})',
  interactionsTab: '互动记录 ({count})',
  dealsTab: '交易 ({count})',
  companyNotFound: '公司未找到',
  companyNotFoundDesc: '该公司可能已被删除',

  newCompany: '新建公司',
  editCompanyTitle: '编辑公司',
  companyName: '公司名称',
  companyNamePlaceholder: '公司名称',
  domain: '域名',
  domainPlaceholder: 'example.com',
  website: '网站',
  country: '国家',
  countryPlaceholder: '国家',
  industry: '行业',
  industryPlaceholder: '行业',
  phone: '电话',
  phonePlaceholder: '+86...',
  companySize: '规模',
  sizeSmall: '小型',
  sizeMedium: '中型',
  sizeLarge: '大型',
  sizeEnterprise: '大企业',
  address: '地址',
  addressPlaceholder: '详细地址',
  notes: '备注',
  notesPlaceholder: '内部备注...',
  companyNameRequired: '公司名称为必填项',
  companyCreated: '公司已创建',
  companyUpdated: '公司已更新',

  searchContacts: '搜索联系人...',
  noContactsFound: '未找到联系人',
  adjustSearch: '尝试调整搜索条件',
  createFirstContact: '创建第一个联系人以开始',

  backToContacts: '返回联系人列表',
  editContact: '编辑联系人',
  activityLogTab: '活动日志 ({count})',
  contactNotFound: '联系人未找到',
  contactNotFoundDesc: '该联系人可能已被删除',

  newContact: '新建联系人',
  editContactTitle: '编辑联系人',
  fullName: '姓名',
  fullNamePlaceholder: '完整姓名',
  email: '邮箱',
  emailPlaceholder: 'email@example.com',
  jobTitle: '职位',
  jobTitlePlaceholder: '例如：采购经理',
  department: '部门',
  departmentPlaceholder: '例如：采购部',
  noCompany: '— 无公司 —',
  nameRequired: '姓名为必填项',
  emailRequired: '邮箱为必填项',
  contactCreated: '联系人已创建',
  contactUpdated: '联系人已更新',

  allStages: '所有阶段',
  allTypes: '所有类型',
  listView: '列表视图',
  kanbanView: '看板视图',
  noDealsFound: '未找到交易',
  createFirstDeal: '创建第一笔交易以开始',

  backToDeals: '返回交易列表',
  dealNotFound: '交易未找到',
  dealNotFoundDesc: '该交易可能已被删除',
  advanceStage: '推进到 {stage}',
  markAsLost: '标记为丢单',
  stageUpdated: '阶段已更新',
  dealMarkedLost: '交易已标记为丢单',
  failedUpdateStage: '更新阶段失败',
  lineItems: '明细行',
  dealInfo: '交易信息',
  activityLog: '活动日志',

  logInteraction: '记录互动',
  interactionTypeNote: '备注',
  interactionTypeCall: '电话',
  interactionTypeMeeting: '会议',
  interactionTypeEmailOut: '邮件（发送）',
  interactionTypeOther: '其他',
  subject: '主题',
  subjectPlaceholder: '简要概述...',
  content: '内容',
  contentPlaceholder: '详情...',
  contentRequired: '内容为必填项',
  interactionLogged: '互动已记录',

  source: '来源',
  tags: '标签',
  createdAt: '创建时间',
  updatedAt: '更新时间',
  amount: '金额',
  currency: '币种',
  probability: '赢率',
  expectedClose: '预计关闭',
  paymentTerms: '付款条件',
  shippingTerms: '运输条件',
  saveFailed: '保存失败',
  deleteFailed: '删除失败',

  // 客户字段
  customerNo: '客户编号',
  customerStatus: '客户状态',
  agentLevel: '代理级别',
  quoteLevel: '报价级别',
  consignee: '收货信息',
  registeredAt: '注册时间',
  lastFollowup: '最后跟进',
  nextFollowup: '下次跟进',
  salesAmount: '销售金额',
  allStatuses: '所有状态',

  // 产品
  searchProducts: '搜索产品...',
  noProductsFound: '未找到产品',
  createFirstProduct: '创建第一个产品以开始',
  addProduct: '新增产品',
  productNo: '产品编号',
  productName: '产品名称',
  unitPrice: '单价',
  category: '产品种类',
  brand: '品牌',
  dimensions: '尺寸',
  netWeight: '净重',
  hsCode: 'HS 编码',
  cartonCapacity: '外箱容量',
  productActive: '启用',
  productInactive: '停用',
  productCreated: '产品已创建',
  productUpdated: '产品已更新',
  productDeleted: '产品已删除',
  deleteProduct: '删除产品',
  nameCn: '中文品名',
  nameEn: '英文品名',
  referencePriceNote: '参考价 — 实际报价 case-by-case',

  // 跟进
  followupRadar: '跟进雷达',
  followupsTab: '跟进 ({count})',
  searchFollowups: '搜索跟进...',
  dueOnly: '仅待跟进 / 即将到期',
  allFollowups: '全部跟进',
  noFollowups: '暂无跟进',
  noFollowupsDue: '暂无待跟进 — 都已处理',
  logFollowup: '记录跟进',
  purpose: '跟进目的',
  purposePlaceholder: '例如：确认付款',
  result: '跟进结果',
  resultPlaceholder: '跟进内容/结果...',
  outcome: '结果状态',
  nextGoal: '下次目标',
  nextGoalPlaceholder: '下次跟进目标...',
  followupCreated: '跟进已记录',
  customer: '客户',
  overdue: '已逾期',
  upcoming: '即将到期',

  // 报价/订单扩展
  goodsSubtotal: '商品小计',
  taxRate: '税率',
  taxAmount: '税费',
  freight: '运费',
  bankFee: '银行手续费',
  discount: '折扣',
  deposit: '定金',
  balance: '尾款',
  received: '已收款',
  deliveryMethod: '交货方式',
  paymentMethod: '支付方式',
  ourCompany: '我方公司',
  salesRep: '销售代表',
  owner: '负责人',
  validUntil: '有效期至',
  expectedDelivery: '预计交货',
  shipTo: '收货信息',
  adapter: '适配器',
  sourceQuote: '来源报价单',
};
